import { useState, RefObject } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setZoomLevel } from '../store/zoomSlice';
import { addPosterToCart, CartItem } from '../store/cartSlice';
import { exportPdf, dataUrlToBlob } from '../utils/pdfUtils';
import CartLoaderOverlay from '../components/CartLoaderOverlay';
import { RootState, AppDispatch } from '../store';
import { Map as MapboxMap } from 'mapbox-gl';
import { EditorPreviewRef } from '../components/EditorPreview';

// Helper pour attendre l'état idle de la carte
const waitForMapIdle = (map: MapboxMap, timeoutMs = 10000): Promise<void> => {
  return new Promise<void>((resolve) => {
    let done = false;
    let timeoutId: NodeJS.Timeout | null = null;

    const onIdle = () => {
      if (!done) {
        done = true;
        if (timeoutId) clearTimeout(timeoutId);
        map.off('idle', onIdle); // S'assurer de détacher l'écouteur quand idle est atteint
        resolve();
      }
    };

    timeoutId = setTimeout(() => {
      if (!done) {
        done = true;
        map.off('idle', onIdle);
        resolve();
      }
    }, timeoutMs);

    map.on('idle', onIdle);
    setTimeout(() => map.resize(), 50); // Garder pour certains cas de redimensionnement initial
  });
};

export function useAddToCart(editorPreviewRef: RefObject<EditorPreviewRef | null>) {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const storeGlobal = useStore<RootState>();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const product = useSelector((state: RootState) => state.product);
  const labels = useSelector((state: RootState) => state.labels);
  const points = useSelector((state: RootState) => state.points.points);
  const layout = useSelector((state: RootState) => state.layout);
  const mapState = useSelector((state: RootState) => state.map);
  const trace = useSelector((state: RootState) => state.trace);
  const profile = useSelector((state: RootState) => state.profile);
  const activitiesStore = useSelector((state: RootState) => state.activities);
  const currentPrice = product.currentPrice;
  const backendUrl = import.meta.env.VITE_BACKEND_URL;

  const handleAddToCart = async () => {
    if (!editorPreviewRef.current || !editorPreviewRef.current.mapInstanceRef?.current) {
      alert(t("overviewPage.editorError"));
      return;
    }
    const mapInstance = editorPreviewRef.current.mapInstanceRef.current;
    const originalZoom = storeGlobal.getState().zoom.selectedZoom;

    setIsAddingToCart(true);
    setIsGeneratingPreview(true);
    setUploadProgress(null);
    let pdfBlob: Blob | null = null;
    let localThumbnailUrl: string | null = null;
    let remotePreviewUrl: string | undefined = undefined;
    let exportSuccess = false;
    const cartItemId = `cart-${Date.now()}-${Math.random().toString(16).substring(2, 8)}`;

    try {
      dispatch(setZoomLevel('100%'));
      await new Promise(resolve => setTimeout(resolve, 100));
      await waitForMapIdle(mapInstance);
      await new Promise(resolve => setTimeout(resolve, 300));
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (editorPreviewRef.current?.containerRef?.current) {
        editorPreviewRef.current.containerRef.current.classList.add('is-exporting');
      }
      try {
        if (editorPreviewRef.current?.generatePreviewImage) {
          localThumbnailUrl = await editorPreviewRef.current.generatePreviewImage();
        }
      } catch (error) {
        console.error("Erreur lors de la génération de l'aperçu (useAddToCart):", error);
      } finally {
        if (editorPreviewRef.current?.containerRef?.current) {
          editorPreviewRef.current.containerRef.current.classList.remove('is-exporting');
        }
        setIsGeneratingPreview(false);
      }

      if (!editorPreviewRef.current?.containerRef || !editorPreviewRef.current?.mapContainerRef || !editorPreviewRef.current?.mapInstanceRef) {
        throw new Error('Références essentielles manquantes pour l\'export PDF.');
      }
      pdfBlob = await exportPdf(
        editorPreviewRef.current.containerRef,
        editorPreviewRef.current.mapContainerRef,
        editorPreviewRef.current.mapInstanceRef
      );
      if (!pdfBlob) throw new Error('PDF non généré');
      exportSuccess = true;

      if (exportSuccess) {
        setUploadProgress(0);
        const formData = new FormData();
        formData.append('file', pdfBlob, `poster-${cartItemId}.pdf`);
        if (localThumbnailUrl) {
          const previewBlob = dataUrlToBlob(localThumbnailUrl);
          formData.append('previewImage', previewBlob, `preview-${cartItemId}.png`);
        }
        formData.append('cartItemId', cartItemId);

        await new Promise<void>((resolveRequest, rejectRequest) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${backendUrl}/api/upload-pdf-with-preview`, true);
          xhr.responseType = 'json';
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              setUploadProgress(percentComplete);
            }
          };
          xhr.onload = () => {
            setUploadProgress(100);
            if (xhr.status >= 200 && xhr.status < 300) {
              const resp = xhr.response;
              if (resp && typeof resp.previewUrl === 'string') {
                remotePreviewUrl = resp.previewUrl;
              }
              resolveRequest();
            } else {
              rejectRequest(new Error(t("overviewPage.uploadServerError", { status: xhr.statusText || xhr.status })));
            }
          };
          xhr.onerror = () => {
            rejectRequest(new Error(t("overviewPage.uploadNetworkError")));
          };
          xhr.send(formData);
        });

        const productForCart: CartItem['posterConfiguration']['product'] = { ...product, price: currentPrice };
        const posterConfiguration: CartItem['posterConfiguration'] = {
          labels, points, layout, map: mapState, trace, profile, product: productForCart,
          activeActivityIds: activitiesStore.activeActivityIds,
          activitiesData: activitiesStore.activities,
        };
        dispatch(addPosterToCart({
          id: cartItemId,
          configuration: posterConfiguration,
          thumbnailUrl: remotePreviewUrl ?? localThumbnailUrl ?? undefined,
        }));
        // Ajout d'un délai pour laisser le temps à l'overlay de s'afficher
        setTimeout(() => {
          navigate('/cart');
        }, 500);
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout au panier:", error);
      alert(t("overviewPage.addToCartError"));
    } finally {
      setIsAddingToCart(false);
      dispatch(setZoomLevel(originalZoom));
    }
  };

  let loaderMessage = '';
  if (isAddingToCart || isGeneratingPreview) {
    if (isGeneratingPreview) {
      loaderMessage = t('overviewPage.generatingPreview', 'Préparation de l\'aperçu et du PDF...');
    } else if (uploadProgress !== null && uploadProgress < 100) {
      loaderMessage = t('overviewPage.uploadingPdf', `Upload du PDF (${uploadProgress}%)...`, { progress: uploadProgress });
    } else {
      loaderMessage = t('overviewPage.addingToCart', 'Ajout au panier en cours...');
    }
  }

  const cartOverlay = (isAddingToCart || isGeneratingPreview) ? (
    <CartLoaderOverlay message={loaderMessage} />
  ) : null;

  return {
    isAddingToCart,
    isGeneratingPreview,
    uploadProgress,
    handleAddToCart,
    cartOverlay,
  };
}
